import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import RacePage from "./AppPages/RacePage";
import CreateAccount from "./AppPages/CreateAccount";
import SignIn from "./AppPages/SignIn";
import TreeLoader from "./AppPages/JoyOfFallTree/TreeLoader";
import { useAuth } from "./context/AuthProvider";
import { AccountIcon, BellIcon, CalendarIcon, ChatIcon, HeartIcon, PaperPlaneIcon, TextBubbleIcon } from "./AppPages/util/Icons";
import HomePage from "./AppPages/UserPage/HomePage";
import ResetPassword from "./AppPages/ResetPassword";
import ReferralLandingPage from "./AppPages/ReferralLandingPage";
import CelebrateCanada from "./AppPages/CelebrateCanada";
import ImageGenerator from "./AppPages/ImageGenerator";
import BigCanadaRunSpaceDemo from "./AppPages/BigCanadaRunSpaceDemo";
import { SendPageView } from "./func/analyticsFunctions";
import { useUserContext } from "./context/UserProvider";
import { collection, doc, getDocs, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { db } from "./firebase";
import DropDownMenu from "./AppPages/util/DropDownMenu";
import ProfilePicture from "./AppPages/util/ProfilePicture";
import { getEligibleTeamChats } from "./AppPages/UserPage/TeamChat/teamChatUtils";
import AppHeader from "./AppPages/util/AppHeader";

// If the page's code fails to download (e.g. a new version was just deployed), reload once to get the new files.
const JoyOfFallTree = lazy(() => import("./AppPages/JoyOfFallTree").then((module) => {
  try { sessionStorage.removeItem("jof-tree-reloaded"); } catch (e) {}
  return module;
}, (error) => {
  let reloaded = false;
  try { reloaded = sessionStorage.getItem("jof-tree-reloaded") === "1"; sessionStorage.setItem("jof-tree-reloaded", "1"); } catch (e) {}
  if (!reloaded) { window.location.reload(); return new Promise(() => {}); }
  throw error;
}));

function App() {
  const location = useLocation();
  const { user, userLoading } = useAuth();
  const isTeamChatRoute = Boolean(user && /^\/team-chat(?:\/|$)/.test(location.pathname));
  // console.log(window.location.pathname + window.location.search)
  // console.log(location.state?.from)
  useEffect(() => {
    // Set Page Title
    let lo = location.pathname;
    switch (true) {
      case lo === "/register":
        document.title = "Referral Page";
        SendPageView("Referral Page");
        break;
      case lo.startsWith("/register/CelebrateCanada"):
        document.title = "Medal Selection - CCC";
        SendPageView("Medal Selection - CCC");
        break;
      case lo === "/signin":
        document.title = "Sign In";
        SendPageView("Sign In");
        break;
      case lo === "/createaccount":
        document.title = "Create Account";
        SendPageView("Create Account");
        break;
      case lo === "/resetpassword":
        document.title = "Password Reset";
        SendPageView("Password Reset");
        break;
      case lo === "/image-generator":
        document.title = "My Challenge Canada Playground";
        SendPageView("My Challenge Canada Playground");
        break;
      case lo === "/image-generator/plushie-avatar":
        document.title = "Plushie Generator - MCC";
        SendPageView("Plushie Generator - MCC");
        break;
      case lo === "/image-generator/photo-booth":
        document.title = "Photo Booth - MCC";
        SendPageView("Photo Booth - MCC");
        break;
      case lo === "/image-generator/memory-game":
        document.title = "Flip the Feed - MCC";
        SendPageView("Flip the Feed - MCC");
        break;
      case lo === "/image-generator/fall-bingo":
        document.title = "Fall Bingo - MCC";
        SendPageView("Fall Bingo - MCC");
        break;
      case lo === "/TBCR-moon-challenge":
        document.title = "Earth To Moon Global Challenge";
        SendPageView("Earth To Moon Global Challenge");
        break;
    }
    // console.log(location, document.title);
  }, [location.pathname]);
  if (userLoading) {
    return;
  }
  return (
    <div
      className={
        "relative grid " +
        (isTeamChatRoute
          ? "h-[100dvh] grid-rows-[auto,minmax(0,1fr)] overflow-hidden"
          : "min-h-screen grid-rows-[auto,1fr,auto]")
      }
    >
      <Header user={user} />
      <div className={"relative min-w-0 " + (isTeamChatRoute ? "min-h-0 overflow-hidden" : "")}>
        <div className={"h-full w-full px-3 mb-0 " + (isTeamChatRoute ? "min-h-0" : "")}>
          <Routes>
            <Route path="joy-of-fall/tree" element={user ? <Suspense fallback={<TreeLoader fullPage />}><JoyOfFallTree /></Suspense> : <Navigate to="/signin" replace state={{ from: "/joy-of-fall/tree" }} />} />
            {user ? (
              <>
                <Route
                  path="/*"
                  element={
                    <div className={"h-full w-full " + (isTeamChatRoute ? "min-h-0" : "mb-5")}>
                      <HomePage />
                    </div>
                  }
                />
              </>
            ) : (
              <>
                <Route
                  path="challenges"
                  element={
                    <Navigate
                      to="/signin"
                      replace
                      state={{ from: location.pathname + location.search + location.hash }}
                    />
                  }
                />
                <Route
                  path="account/delete"
                  element={
                    <Navigate
                      to="/signin"
                      replace
                      state={{ from: "/account/delete" }}
                    />
                  }
                />
              </>
            )}

            <Route path="register/" element={<ReferralLandingPage />} />

            <Route path="register/CelebrateCanada/*" element={<CelebrateCanada />} />

            <Route path="register/:id/*" element={<RacePage />} />

            <Route path="TBCR-moon-challenge" element={<BigCanadaRunSpaceDemo />} />
            <Route path="big-canada-run-space-demo" element={<Navigate to="/TBCR-moon-challenge" replace />} />

            {/* REDIRECTS */}
            <Route path="/register/thebigcanadarun2024" element={<Navigate to="/register/thebigcanadarun2025" />} />

            <Route
              path="signin"
              element={
                // <div className="mt-[35px]">
                // <div>
                <SignIn />
                // </div>
              }
            />
            <Route
              path="createaccount"
              element={
                // <div className="mt-[35px]">
                // <div>
                <CreateAccount />
                // </div>
              }
            />
            <Route
              path="resetpassword"
              element={
                <div className="mt-5">
                  <ResetPassword />
                </div>
              }
            />
            <Route path="*" element={<Navigate to="createaccount" replace={true} state={{from: location.pathname + location.search + location.hash}} />} />
            {/* <Route path="404" element={<h1>404</h1>} /> */}
            <Route path="image-generator/*" element={<ImageGenerator />} />
          </Routes>
        </div>
      </div>

      {!isTeamChatRoute && <Footer />}
    </div>
  );
}

export default App;

function Header({ user }) {
  const currentLocation = useLocation();
  const navigate = useNavigate();
  const showBackButton = /^\/dashboard\/|^\/account|^\/leaderboard\//.test(currentLocation.pathname);

  function goBack() {
    navigate(-1);
  }
  let color = "black";
  if (window.location.origin === "https://mcc-staging-4525c.web.app" || window.location.origin === "http://localhost:3000") {
    color = "darkorange";
  }

  return (
    <AppHeader backgroundColor={color} showBackButton={showBackButton} onBack={goBack}>
      <div className="h-full">{user && <StreakButton />}</div>
      <div className="h-full">{user && <TeamChatButton />}</div>
      <div className="h-full">{user && <NotificationButton />}</div>
      <div className="h-full">{user && <AccountButton />}</div>
    </AppHeader>
  );
}

function Footer() {
  return (
    <footer className="w-full bg-black text-white text-center text-xs py-4 ">
      <p className="mb-2">Made With 🍁 In Canada</p>
      <p className="mb-2 underline">hello@mychallengecanada.ca</p>
      <img className="mx-auto h-6 mb-2" src={process.env.PUBLIC_URL + `/img/api_logo_cptblWith_strava_horiz_white.png`} alt="Strava Logo" />
      <a
        href="https://mychallengecanada.ca/cookies-terms-and-privacy-policy/"
        target="_blank"
        rel="noreferrer"
        className="text-white pb-3 block"
      >
        Cookies, Privacy And Terms Policies
      </a>
      <p className="mb-0"> © My Challenge Canada 2025</p>
    </footer>
  );
}

function getMillisFromFirestoreValue(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();

  const parsedTime = new Date(value).getTime();
  return Number.isFinite(parsedTime) ? parsedTime : 0;
}

function TeamChatButton() {
  const { user, userRaces } = useUserContext();
  const teamChats = useMemo(() => getEligibleTeamChats(userRaces), [userRaces]);
  const eligibleChatIds = useMemo(() => new Set(teamChats.map((chat) => chat.id)), [teamChats]);
  const [chatNotifications, setChatNotifications] = useState(new Map());
  const [chatSummaries, setChatSummaries] = useState(new Map());
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.uid || teamChats.length === 0) {
      setChatNotifications(new Map());
      return undefined;
    }

    return onSnapshot(
      collection(db, "users", user.uid, "chat_notifications"),
      (snap) => {
        const nextNotifications = new Map();
        snap.docs.forEach((notificationDoc) => {
          if (!eligibleChatIds.has(notificationDoc.id)) return;
          nextNotifications.set(notificationDoc.id, notificationDoc.data());
        });
        setChatNotifications(nextNotifications);
      },
      (error) => {
        console.error("Unable to load team chat notifications", error);
        setChatNotifications(new Map());
      }
    );
  }, [eligibleChatIds, teamChats.length, user?.uid]);

  useEffect(() => {
    setChatSummaries(new Map());
    if (!user?.uid || teamChats.length === 0) return undefined;

    const unsubscribes = teamChats.map((chat) => {
      return onSnapshot(
        doc(db, "team_chats", chat.id),
        (snap) => {
          setChatSummaries((prev) => {
            const next = new Map(prev);
            if (snap.exists()) {
              next.set(chat.id, snap.data());
            } else {
              next.delete(chat.id);
            }
            return next;
          });
        },
        (error) => {
          console.error("Unable to load team chat summary", error);
        }
      );
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [teamChats, user?.uid]);

  useEffect(() => {
    const nextUnreadCount = teamChats.reduce((total, chat) => {
      const notification = chatNotifications.get(chat.id) || {};
      const notificationUnread = Math.max(0, Number(notification.unreadCount || 0));
      if (notificationUnread > 0) return total + notificationUnread;

      const summary = chatSummaries.get(chat.id);
      const lastMessageAt = getMillisFromFirestoreValue(summary?.lastMessageAt);
      const lastReadAt = getMillisFromFirestoreValue(notification.lastReadAt);
      if (
        lastMessageAt > lastReadAt &&
        summary?.lastMessageAuthorId &&
        summary.lastMessageAuthorId !== user?.uid
      ) {
        return total + 1;
      }

      return total;
    }, 0);

    setUnreadCount(nextUnreadCount);
  }, [chatNotifications, chatSummaries, teamChats, user?.uid]);

  if (!user?.uid || teamChats.length === 0) return null;

  const badgeText = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <Link
      to="/team-chat"
      aria-label={unreadCount > 0 ? `Open team chat. ${unreadCount} unread message${unreadCount === 1 ? "" : "s"}.` : "Open team chat"}
      title="Team chat"
      className="relative h-full w-10 sm:w-12 flex items-center justify-center hover:bg-black/20"
    >
      <ChatIcon color="white" size={24} />
      {unreadCount > 0 && (
        <span className="absolute top-1.5 right-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white">
          {badgeText}
        </span>
      )}
    </Link>
  );
}

function NotificationButton() {
  const { user } = useUserContext();
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    async function getNotifs() {
      if (!user?.uid) return;
      // console.warn("%cFetching notifications", "color: yellow");
      try {
        const notifRef = query(
          collection(db, "users", user.uid, "notifications"),
          where("read", "==", false),
          orderBy("lastUpdatedAt", "desc")
        );
        const notifSnap = await getDocs(notifRef);
        setNotifications(notifSnap.docs);
      } catch (error) {
        console.error("Unable to load notifications", error);
        setNotifications([]);
      }
    }
    getNotifs();
  }, [user?.uid]);
  // console.log(notifications);
  async function updateReadNotif(notifId) {
    try {
      // console.log("making notif read");
      // Maybe make it so if the post the notification belongs to no longer exists, delet the notif
      let notifIndex = notifications.findIndex((n) => n.id === notifId);
      if (notifIndex < 0) return;
      const notifSnap = notifications[notifIndex];
      await updateDoc(doc(db, "users", user.uid, "notifications", notifId), {
        read: true,
        lastSeenAt: Date.now(),
        seenCount: notifSnap.data().count,
      });
      let updatedNotifs = [...notifications];
      let nIndex = updatedNotifs.findIndex((n) => n.id === notifId);
      updatedNotifs[nIndex].clientRead = true;
      setNotifications(updatedNotifs);
    } catch (err) {
      console.error(err.message);
    }
  }
  return (
    <div className=" h-full flex justify-end items-center">
      <DropDownMenu
        trigger={({ ref, onClick, open }) => (
          <button
            ref={ref}
            onPointerDown={onClick}
            style={{ backgroundColor: open ? "#0005" : "transparent" }}
            className="w-10 sm:w-12 h-full relative flex items-center justify-center"
          >
            <BellIcon color="white" />
            {notifications.filter((n) => !n.clientRead).length > 0 && (
              <span className="absolute top-1/4 left-1/2 flex items-center justify-center m-0 rounded-full w-5 h-5 bg-red-600 text-white text-xs font-bold leading-none pointer-events-none">
                {notifications.filter((n) => !n.clientRead).length}
              </span>
            )}
          </button>
        )}
      >
        <div className="bg-white flex flex-col gap-1 w-80 p-2 rounded-2xl shadow-md">
          {notifications.length === 0 && <span className="w-full px- py-1">No new notifications...</span>}
          {notifications.map((notif) => {
            const data = notif.data();
            const type = data.type;
            return (
              <NotificationCard
                key={`notif_${notif?.id}`}
                activity={data.activity}
                type={type}
                postId={data?.postId}
                read={notif.clientRead}
                onClick={() => {
                  updateReadNotif(notif?.id);
                }}
              />
            );
          })}
        </div>
      </DropDownMenu>
    </div>
  );
}

function NotificationCard({ postId, activity = "notification", type = "post", read = false, onClick = () => { } }) {
  // Default for comments
  let icon = <TextBubbleIcon size={24} style="fill" color="#3b82f6" />;
  if (activity === "likes") {
    icon = <HeartIcon size={24} style="fill" color="#f53b3b" />;
  }
  if (activity === "reply") {
    icon = <PaperPlaneIcon size={24} style="fill" color="#22c55e" />;
  }
  if (activity === "comments") {
  }
  return (
    <Link
      to={`/p/${postId}`}
      onClick={onClick}
      className="relative bg-slate-100 hover:bg-slate-200 rounded-2xl w-full px-3 py-3 flex flex-row gap-3 items-center transition-colors no-underline"
    >
      {!read && <i className="m-0 absolute rounded-full h-2 w-2 top-2 right-2 bg-red-600"></i>}
      <div className="text-[#007bff]">{icon}</div>
      <span className="font-medium text-black text-sm leading-tight">
        Your {type} has new {activity}!
      </span>
    </Link>
  );
}

function StreakButton() {
  const { canViewStreaks, currentStreakWeeks } = useUserContext();
  const showBadge = canViewStreaks;
  const badgeText = currentStreakWeeks > 99 ? "99+" : String(currentStreakWeeks);
  const streakLabel = showBadge
    ? `Open streaks. Current streak: ${currentStreakWeeks} week${currentStreakWeeks === 1 ? "" : "s"}.`
    : "Open streaks";

  return (
    <Link
      to="/streaks"
      aria-label={streakLabel}
      title={showBadge ? `${currentStreakWeeks} week streak` : "Streaks"}
      className="relative h-full w-10 sm:w-12 flex items-center justify-center hover:bg-black/20"
    >
      <CalendarIcon color="white" size={24} />
      {showBadge && (
        <span
          className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full border border-white text-[10px] font-bold leading-none text-white flex items-center justify-center"
          style={{
            backgroundColor: currentStreakWeeks > 0 ? "#ff6a00" : "#64748b",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.24)",
          }}
        >
          {badgeText}
        </span>
      )}
    </Link>
  );
}

function AccountButton() {
  const { user, logOut } = useAuth();
  const { userDetails } = useUserContext();
  const displayName = [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(" ") || user?.displayName || user.email?.split("@")[0];

  return (
    <div className=" h-full flex justify-end items-center">
      <DropDownMenu
        trigger={({ ref, onClick, open }) => (
          <button
            ref={ref}
            onPointerDown={onClick}
            style={{ backgroundColor: open ? "#0005" : "transparent" }}
            className="w-10 sm:w-12 h-full flex items-center justify-center"
          >
            <AccountIcon color="white" />
          </button>
        )}
      >
        <div className="bg-white w-[260px] max-w-[calc(100vw-1rem)] rounded-xl shadow-xl px-4 py-3.5" data-noclose>
          <div className="flex items-center gap-3 mb-3" data-noclose>
            <ProfilePicture
              userID={user?.uid}
              name={displayName}
              color=" bg-gray-400 text-white"
              className=" !w-12 !h-12 text-base flex-shrink-0"
            />
            <div className="min-w-0" data-noclose>
              <p className="m-0 text-[15px] font-semibold leading-tight text-gray-950 truncate" data-noclose>
                {displayName}
              </p>
              <p className="m-0 text-[13px] leading-tight text-gray-500 truncate" data-noclose>
                {user.email}
              </p>
            </div>
          </div>
          <AccountMenuLink to="/" icon={<HomeIcon />}>
            Homepage
          </AccountMenuLink>
          <AccountMenuLink to="/account" icon={<SettingsIcon />}>
            Account Settings
          </AccountMenuLink>
          <AccountMenuLink to={`/profile/${user?.uid}`} icon={<ProfileMenuIcon />}>
            My Profile
          </AccountMenuLink>
          <AccountMenuLink to="/referralhub" icon={<ReferralIcon />}>
            Referral Hub
          </AccountMenuLink>
          <AccountMenuLink to="/membership" icon={<StarIcon />}>
            Membership
          </AccountMenuLink>
          <AccountMenuLink to="/store" icon={<StoreMenuIcon />}>
            Store
          </AccountMenuLink>
          <hr className="my-2.5 border-gray-100" />
          <button
            onClick={(e) => logOut()}
            className="cursor-pointer w-full flex items-center gap-3 rounded-lg py-1.5 px-1 text-left text-[#c97777] hover:bg-red-50 transition-colors"
          >
            <PowerIcon />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </DropDownMenu>
    </div>
  );
}

function AccountMenuLink({ to, icon, children }) {
  return (
    <Link
      to={to}
      className="cursor-pointer no-underline flex items-center gap-3 rounded-lg py-1.5 px-1 text-gray-950 hover:bg-slate-100 transition-colors"
    >
      {icon}
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );
}

function AccountMenuSvg({ children, color = "#111827" }) {
  return (
    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-current">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </span>
  );
}

function HomeIcon() {
  return (
    <AccountMenuSvg color="#2563eb">
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10.5V20h5v-5.5h3V20h5v-9.5" />
    </AccountMenuSvg>
  );
}

function SettingsIcon() {
  return (
    <AccountMenuSvg color="#475569">
      <path d="M12 3.5v3" />
      <path d="M12 17.5v3" />
      <path d="M4.6 7.8l2.6 1.5" />
      <path d="M16.8 14.7l2.6 1.5" />
      <path d="M19.4 7.8l-2.6 1.5" />
      <path d="M7.2 14.7l-2.6 1.5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="12" cy="12" r="1.4" />
    </AccountMenuSvg>
  );
}

function ProfileMenuIcon() {
  return (
    <AccountMenuSvg color="#10b981">
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20c1.5-4 13.5-4 15 0" />
    </AccountMenuSvg>
  );
}

function ReferralIcon() {
  return (
    <AccountMenuSvg color="#8b5cf6">
      <circle cx="8" cy="8.5" r="3" />
      <circle cx="17" cy="9.5" r="2.5" />
      <path d="M2.8 19c1.1-4 9.3-4 10.4 0" />
      <path d="M13.5 18.5c1.2-2.3 6.4-2.3 7.7 0" />
    </AccountMenuSvg>
  );
}

function StarIcon() {
  return (
    <AccountMenuSvg color="#f59e0b">
      <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8-5.4 2.8 1-6-4.4-4.3 6.1-.9Z" />
    </AccountMenuSvg>
  );
}

function StoreMenuIcon() {
  return (
    <AccountMenuSvg color="#0f766e">
      <path d="M4 8h16l-1.5 11h-13L4 8Z" />
      <path d="M8 8a4 4 0 0 1 8 0" />
      <path d="M9 12h.01" />
      <path d="M15 12h.01" />
    </AccountMenuSvg>
  );
}

function PowerIcon() {
  return (
    <AccountMenuSvg color="#c97777">
      <path d="M12 3v8" />
      <path d="M7.1 6.8a8 8 0 1 0 9.8 0" />
    </AccountMenuSvg>
  );
}
